// This file exists to provide an API for piece-to-piece interactions and moves.
import { Euler, Quaternion, Vector3 } from "three";

import { PieceFacts, PieceId, Square, SquareFacts, square } from "src/api";
import {
  ATTACK_DURATION_SECONDS,
  MOVE_DURATION_SECONDS_PER_UNIT,
  ROTATION_DURATION_SECONDS,
  SQUARE_UNITS
} from "src/client/human/render/renderConstants";
import { resolveFacingQuaternionAfterMove } from "src/client/human/render/utilities/vectorUtils";

import { BoardUpdate } from "../updates/BoardUpdate";
import { squareToWorld } from "../utilities/boardCoordinates";

export type SequencedMoveAction = "moving" | "attacking" | "attacked" | "dying";

export type SequencedMove = {
  timeOffsetSeconds: number;
  timeDurationSeconds: number;
  pieceId: PieceId;
  action: SequencedMoveAction;
  fromPosition?: Vector3;
  toPosition?: Vector3;
  fromQuaternion?: Quaternion;
  toQuaternion?: Quaternion;
};

export type SequencedMoves = SequencedMove[];

export type MoveSequenceAPI = {
  squares: Set<Square>;
  pieceIds: Set<PieceId>;
  createsPieceIds?: Set<PieceId>;
  destroysPieceIds?: Set<PieceId>;
  moves: SequencedMoves;
};

export function directionFromPieceFacts(pieceFacts: PieceFacts) {
  switch (pieceFacts.facing ?? "N") {
    case "N":
      return Math.PI;
    case "E":
      return Math.PI / 2;
    case "S":
      return 0;
    case "W":
      return (Math.PI * 3) / 2;
  }
}

export function directionFromSquareFacts(squareFacts: SquareFacts) {
  return squareFacts.piece ? directionFromPieceFacts(squareFacts.piece) : 0;
}

export function generateMoveSequenceForBoardUpdate(
  previousSquareFacts: ReadonlyMap<Square, SquareFacts>,
  update: BoardUpdate
): MoveSequenceAPI | null {
  const squares = new Set<Square>();
  const pieceIds = new Set<PieceId>();
  const createsPieceIds = new Set<PieceId>();
  const destroysPieceIds = new Set<PieceId>();

  const idToBefore = new Map<PieceId, [Square, PieceFacts]>();
  const idToAfter = new Map<PieceId, [Square, PieceFacts]>();

  for (const [square, squareFacts] of previousSquareFacts) {
    if (squareFacts.piece === undefined) continue;
    idToBefore.set(squareFacts.piece.id, [square, squareFacts.piece]);
    squares.add(square);
    pieceIds.add(squareFacts.piece.id);
  }
  for (const [square, squareFacts] of update.facts.squares) {
    if (squareFacts.piece === undefined) continue;
    idToAfter.set(squareFacts.piece.id, [square, squareFacts.piece]);
    squares.add(square);
    pieceIds.add(squareFacts.piece.id);
    if (!idToBefore.has(squareFacts.piece.id))
      createsPieceIds.add(squareFacts.piece.id);
  }
  for (const squareFacts of previousSquareFacts.values()) {
    if (squareFacts.piece === undefined) continue;
    if (idToAfter.has(squareFacts.piece.id)) continue;
    destroysPieceIds.add(squareFacts.piece.id);
  }

  const moves: SequencedMoves = [];

  if (update.delta.kind === "move") {
    const capturedSquares = new Set(update.delta.captures);
    const shoveLegs = update.delta.legs.filter((leg) => leg.role === "shoved");
    const otherLegs = update.delta.legs.filter((leg) => leg.role !== "shoved");
    const shoveLegsMap = new Map(shoveLegs.map((leg) => [leg.from, leg]));
    const shoveLegsHandledSet = new Set<Square>();
    for (const leg of otherLegs) {
      if (leg.id === undefined) continue;
      pieceIds.add(leg.id);
      squares.add(leg.from);
      squares.add(leg.to);
      const prevSquareFacts = previousSquareFacts.get(leg.from);
      const nextSquareFacts = update.facts.squares.get(leg.to);
      const isCaptureOrShove =
        capturedSquares.has(leg.to) || shoveLegsMap.has(leg.to);
      let t = 0;
      let dt = 0;
      const startPosition = squareToWorld(leg.from);
      const endPosition = squareToWorld(leg.to);
      const moveDistance = endPosition.clone().sub(startPosition).length();
      let startDirection = 0;
      let endDirection = 0;
      if (prevSquareFacts !== undefined)
        startDirection = directionFromSquareFacts(prevSquareFacts);
      if (nextSquareFacts !== undefined)
        endDirection = directionFromSquareFacts(nextSquareFacts);
      const startQuaternion = new Quaternion().setFromEuler(
        new Euler(0, startDirection, 0)
      );
      const endQuaternion = new Quaternion().setFromEuler(
        new Euler(0, endDirection, 0)
      );
      const moveQuaternion = resolveFacingQuaternionAfterMove(
        startPosition,
        endPosition,
        false
      );

      // If this move is a capture or a shove, we need to pause motion to attack prior to effecting
      // the other piece.
      if (isCaptureOrShove) {
        const attackedPieceId = previousSquareFacts.get(leg.to)?.piece?.id;
        const attackDistance = Math.max(0, moveDistance - SQUARE_UNITS);
        const attackPosition = endPosition
          .clone()
          .sub(startPosition)
          .normalize()
          .multiplyScalar(attackDistance)
          .add(startPosition);
        dt = ROTATION_DURATION_SECONDS;
        moves.push({
          timeOffsetSeconds: t,
          timeDurationSeconds: dt,
          pieceId: leg.id,
          action: "moving",
          fromPosition: startPosition,
          toPosition: startPosition,
          fromQuaternion: startQuaternion,
          toQuaternion: moveQuaternion
        });
        t += dt;
        // If we have any ground to cover before the attack, do so.
        if (attackDistance + 0.1 > 0) {
          // Cover ground before the attack.
          dt = attackDistance * MOVE_DURATION_SECONDS_PER_UNIT;
          moves.push({
            timeOffsetSeconds: t,
            timeDurationSeconds: dt,
            pieceId: leg.id,
            action: "moving",
            fromPosition: startPosition,
            toPosition: attackPosition,
            fromQuaternion: moveQuaternion,
            toQuaternion: moveQuaternion
          });
          t += dt;
          dt = ATTACK_DURATION_SECONDS;
          moves.push({
            timeOffsetSeconds: t,
            timeDurationSeconds: dt,
            pieceId: leg.id,
            action: "attacking",
            fromPosition: attackPosition,
            toPosition: attackPosition,
            fromQuaternion: moveQuaternion,
            toQuaternion: moveQuaternion
          });
          if (attackedPieceId) {
            const attackedSquareAndPieceBefore =
              idToBefore.get(attackedPieceId);
            const attackedSquareAndPieceAfter = idToAfter.get(attackedPieceId);
            if (attackedSquareAndPieceBefore !== undefined) {
              if (attackedSquareAndPieceAfter !== undefined) {
                // We're shoving.
                moves.push({
                  timeOffsetSeconds: t,
                  timeDurationSeconds: dt,
                  pieceId: attackedSquareAndPieceBefore[1].id,
                  action: "attacked",
                  fromPosition: squareToWorld(attackedSquareAndPieceBefore[0]),
                  toPosition: squareToWorld(attackedSquareAndPieceAfter[0]),
                  fromQuaternion: new Quaternion().setFromEuler(
                    new Euler(
                      0,
                      directionFromPieceFacts(attackedSquareAndPieceBefore[1])
                    )
                  ),
                  toQuaternion: new Quaternion().setFromEuler(
                    new Euler(
                      0,
                      directionFromPieceFacts(attackedSquareAndPieceAfter[1])
                    )
                  )
                });
                shoveLegsHandledSet.add(leg.to);
              } else {
                // We're destroying.
                moves.push({
                  timeOffsetSeconds: t,
                  timeDurationSeconds: dt,
                  pieceId: attackedSquareAndPieceBefore[1].id,
                  action: "dying",
                  fromPosition: squareToWorld(attackedSquareAndPieceBefore[0]),
                  toPosition: squareToWorld(attackedSquareAndPieceBefore[0]),
                  fromQuaternion: new Quaternion().setFromEuler(
                    new Euler(
                      0,
                      directionFromPieceFacts(attackedSquareAndPieceBefore[1])
                    )
                  ),
                  toQuaternion: new Quaternion().setFromEuler(
                    new Euler(
                      0,
                      directionFromPieceFacts(attackedSquareAndPieceBefore[1])
                    )
                  )
                });
              }
            }
            t += dt;
            dt = MOVE_DURATION_SECONDS_PER_UNIT;
            moves.push({
              timeOffsetSeconds: t,
              timeDurationSeconds: dt,
              pieceId: leg.id,
              action: "moving",
              fromPosition: attackPosition,
              toPosition: endPosition,
              fromQuaternion: moveQuaternion,
              toQuaternion: endQuaternion
            });
          }
        } else {
          // Attack immediately.
          dt = ATTACK_DURATION_SECONDS;
          moves.push({
            timeOffsetSeconds: t,
            timeDurationSeconds: dt,
            pieceId: leg.id,
            action: "attacking",
            fromPosition: attackPosition,
            toPosition: attackPosition,
            fromQuaternion: moveQuaternion,
            toQuaternion: moveQuaternion
          });
          if (attackedPieceId) {
            const attackedSquareAndPieceBefore =
              idToBefore.get(attackedPieceId);
            const attackedSquareAndPieceAfter = idToAfter.get(attackedPieceId);
            if (attackedSquareAndPieceBefore !== undefined) {
              if (attackedSquareAndPieceAfter !== undefined) {
                // We're shoving.
                moves.push({
                  timeOffsetSeconds: t,
                  timeDurationSeconds: dt,
                  pieceId: attackedSquareAndPieceBefore[1].id,
                  action: "attacked",
                  fromPosition: squareToWorld(attackedSquareAndPieceBefore[0]),
                  toPosition: squareToWorld(attackedSquareAndPieceAfter[0]),
                  fromQuaternion: new Quaternion().setFromEuler(
                    new Euler(
                      0,
                      directionFromPieceFacts(attackedSquareAndPieceBefore[1])
                    )
                  ),
                  toQuaternion: new Quaternion().setFromEuler(
                    new Euler(
                      0,
                      directionFromPieceFacts(attackedSquareAndPieceAfter[1])
                    )
                  )
                });
                shoveLegsHandledSet.add(leg.to);
              } else {
                // We're destroying.
                moves.push({
                  timeOffsetSeconds: t,
                  timeDurationSeconds: dt,
                  pieceId: attackedSquareAndPieceBefore[1].id,
                  action: "dying",
                  fromPosition: squareToWorld(attackedSquareAndPieceBefore[0]),
                  toPosition: squareToWorld(attackedSquareAndPieceBefore[0]),
                  fromQuaternion: new Quaternion().setFromEuler(
                    new Euler(
                      0,
                      directionFromPieceFacts(attackedSquareAndPieceBefore[1])
                    )
                  ),
                  toQuaternion: new Quaternion().setFromEuler(
                    new Euler(
                      0,
                      directionFromPieceFacts(attackedSquareAndPieceBefore[1])
                    )
                  )
                });
              }
            }
            t += dt;
            dt = MOVE_DURATION_SECONDS_PER_UNIT;
            moves.push({
              timeOffsetSeconds: t,
              timeDurationSeconds: dt,
              pieceId: leg.id,
              action: "moving",
              fromPosition: attackPosition,
              toPosition: endPosition,
              fromQuaternion: moveQuaternion,
              toQuaternion: endQuaternion
            });
          }
        }
        continue;
      }

      // Determine if the piece turns, moves, and turns, or if it will move while turning.
      if (moveDistance + 0.1 > SQUARE_UNITS * Math.SQRT2) {
        // Moves before and after turn.
        dt = ROTATION_DURATION_SECONDS;
        moves.push({
          timeOffsetSeconds: t,
          timeDurationSeconds: dt,
          pieceId: leg.id,
          action: "moving",
          fromPosition: startPosition,
          toPosition: startPosition,
          fromQuaternion: startQuaternion,
          toQuaternion: moveQuaternion
        });
        t += dt;
        dt = moveDistance * MOVE_DURATION_SECONDS_PER_UNIT;
        moves.push({
          timeOffsetSeconds: t,
          timeDurationSeconds: dt,
          pieceId: leg.id,
          action: "moving",
          fromPosition: startPosition,
          toPosition: endPosition,
          fromQuaternion: moveQuaternion,
          toQuaternion: moveQuaternion
        });
        t += dt;
        dt = ROTATION_DURATION_SECONDS;
        moves.push({
          timeOffsetSeconds: t,
          timeDurationSeconds: dt,
          pieceId: leg.id,
          action: "moving",
          fromPosition: endPosition,
          toPosition: endPosition,
          fromQuaternion: moveQuaternion,
          toQuaternion: endQuaternion
        });
        t += dt;
      } else {
        // Moves while turning.
        dt = moveDistance * MOVE_DURATION_SECONDS_PER_UNIT;
        moves.push({
          timeOffsetSeconds: t,
          timeDurationSeconds: dt,
          pieceId: leg.id,
          action: "moving",
          fromPosition: startPosition,
          toPosition: endPosition,
          fromQuaternion: startQuaternion,
          toQuaternion: endQuaternion
        });
        t += dt;
      }
    }

    // Handle any remaining shoves (this could be more graceful).
    for (const leg of shoveLegs) {
      if (leg.id === undefined || shoveLegsHandledSet.has(leg.from)) continue;
      // This shouldn't occur, but might.
      const pieceFactsBefore = idToBefore.get(leg.id);
      const pieceFactsAfter = idToAfter.get(leg.id);
      if (!pieceFactsBefore || !pieceFactsAfter) continue;
      pieceIds.add(leg.id);
      const t = 0;
      const dt = MOVE_DURATION_SECONDS_PER_UNIT;
      moves.push({
        timeOffsetSeconds: t,
        timeDurationSeconds: dt,
        pieceId: leg.id,
        action: "moving",
        fromPosition: squareToWorld(leg.from),
        toPosition: squareToWorld(leg.to),
        fromQuaternion: new Quaternion().setFromEuler(
          new Euler(0, directionFromPieceFacts(pieceFactsBefore[1]), 0)
        ),
        toQuaternion: new Quaternion().setFromEuler(
          new Euler(0, directionFromPieceFacts(pieceFactsAfter[1]), 0)
        )
      });
    }

    return {
      squares,
      pieceIds,
      createsPieceIds,
      destroysPieceIds,
      moves
    };
  }

  return null;
}
